import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router'
import { LanguageSwitch } from '#app/routes/resources/locale-switch.tsx'
import { ThemeSwitch } from '#app/routes/resources/theme-switch.tsx'
import { DEFAULT_ACCENT } from '#app/utils/accent.ts'
import { cn } from '#app/utils/misc.tsx'
import { useOptionalRequestInfo } from '#app/utils/request-info.ts'
import { useOptionalUser } from '#app/utils/user.ts'
import {
	accountCtaLink,
	isLinkActive,
	resolveNavbar,
	type NavbarVariant,
	type NavbarVisibility,
} from './app-shell-nav.ts'
import { Logo } from './logo.tsx'
import { NavbarDrawer } from './navbar-drawer.tsx'
import { NotificationBell } from './notifications/notification-bell.tsx'
import { useRoutePending } from './route-pending.tsx'
import { ThemeCustomizer } from './theme-customizer.tsx'
import { Button } from './ui/button.tsx'
import { SidebarRail, type SidebarGroup } from './ui/sidebar.tsx'
import { UserDropdown } from './user-dropdown.tsx'

/**
 * The shared chrome frame for every non-marketing surface (ADR-068): a universal
 * top navbar, an optional section sidebar slot, and the content column. Each
 * section layout wraps its outlet with this; `root.tsx` no longer renders any
 * generic chrome (ADR-068), so every surface owns its frame explicitly. The
 * navbar variant drives what the chrome shows — `full` is the whole-product
 * bar (account/admin), `marketing` is the public bar (landing/blog), and
 * `minimal` is the auth pass-through.
 *
 * On account/admin the section nav is supplied as `sidebarGroups` config (not a
 * pre-rendered node): the shell renders the desktop rail from it via the shared
 * `SidebarNav`, and the navbar's mobile drawer renders the **same** groups — so
 * the rail and the drawer can never drift, and there is exactly one mobile menu
 * (the navbar hamburger). Single-page surfaces like the public blog pass none.
 */
export function AppShell({
	variant = 'full',
	sidebarGroups,
	sidebarLabel = 'Section',
	children,
}: {
	variant?: NavbarVariant
	/** Section nav groups for the account/admin rail + the navbar drawer. */
	sidebarGroups?: SidebarGroup[]
	/** Accessible label for the section nav (e.g. "Account", "Admin"). */
	sidebarLabel?: string
	children: React.ReactNode
}) {
	const location = useLocation()
	// Stale-content dim (EPT-193): while a navigation is pending, the outgoing
	// content column fades and goes inert; the navbar (with its halo'd logo) stays
	// lit. Restores instantly on arrival — no transition, no crossfade.
	const pending = useRoutePending()
	const contentDim = pending && 'opacity-[.55] pointer-events-none'
	return (
		<div className="bg-background text-foreground flex min-h-screen flex-col">
			<AppNavbar
				variant={variant}
				sidebarGroups={sidebarGroups}
				sidebarLabel={sidebarLabel}
			/>
			{sidebarGroups ? (
				<div className="flex flex-1">
					{/* Pinned under the sticky navbar (h-15 = 3.75rem): the rail fills the
					    remaining viewport and stays put while the content column scrolls;
					    its own items scroll internally if they ever outgrow the height. */}
					<SidebarRail
						groups={sidebarGroups}
						label={sidebarLabel}
						pathname={location.pathname}
						linkComponent={Link}
						className="sticky top-15 h-[calc(100svh-3.75rem)] overflow-y-auto"
					/>
					<div className={cn('flex min-w-0 flex-1 flex-col', contentDim)}>
						{children}
					</div>
				</div>
			) : (
				<div className={cn('flex flex-1 flex-col', contentDim)}>{children}</div>
			)}
		</div>
	)
}

/**
 * Frames a full-page error/404 boundary in the universal navbar (`full`) so a
 * dead end still carries chrome to escape from (ADR-068). It falls back to the
 * bare children when the root loader itself failed — without its request info
 * the navbar's theme/accent switches can't render, and that genuine last resort
 * shouldn't crash the boundary. Section surfaces already nest their boundaries
 * inside their own `AppShell`; this is for the unframed dead ends (root + the
 * `/` splat 404).
 */
export function AppShellBoundary({ children }: { children: React.ReactNode }) {
	const requestInfo = useOptionalRequestInfo()
	return requestInfo ? (
		<AppShell variant="full">{children}</AppShell>
	) : (
		<>{children}</>
	)
}

/**
 * Scrollspy for the navbar's in-page anchor links: observes the landing section
 * elements (by id) and returns whichever is currently in view, so the matching
 * nav link can highlight as the visitor scrolls. Pure progressive enhancement —
 * the resting state is `null` (no section active), and where the sections don't
 * exist (e.g. the blog) the observer simply finds nothing. Guards on
 * `IntersectionObserver` so SSR and jsdom are no-ops.
 */
function useActiveSection(anchorIds: Array<string>): string | null {
	const [active, setActive] = React.useState<string | null>(null)
	// Join to a primitive so the effect only re-subscribes when the ids change,
	// not on every render's fresh array identity.
	const key = anchorIds.join(',')
	React.useEffect(() => {
		if (typeof IntersectionObserver === 'undefined') return
		const ids = key ? key.split(',') : []
		if (ids.length === 0) return
		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (entry.isIntersecting) setActive(entry.target.id)
				}
			},
			// The mid-viewport band: a section counts as in view once it crosses the
			// centre line, matching the retired marketing header's scrollspy feel.
			{ rootMargin: '-50% 0px -50% 0px' },
		)
		for (const id of ids) {
			const el = document.getElementById(id)
			if (el) observer.observe(el)
		}
		return () => observer.disconnect()
	}, [key])
	return active
}

/**
 * True once the page has scrolled past `threshold` pixels. Drives the marketing
 * header's scroll-aware glass: transparent while the nav floats over the hero at
 * the very top, then a frosted, shadowed bar once the visitor scrolls. Pure
 * enhancement — SSR/jsdom start `false` (the resting transparent state), and the
 * initial read on mount catches a restored scroll position without a flash.
 */
function useScrolled(threshold = 8): boolean {
	const [scrolled, setScrolled] = React.useState(false)
	React.useEffect(() => {
		const onScroll = () => setScrolled(window.scrollY > threshold)
		onScroll()
		window.addEventListener('scroll', onScroll, { passive: true })
		return () => window.removeEventListener('scroll', onScroll)
	}, [threshold])
	return scrolled
}

/**
 * The universal top navbar. Its visible affordances are decided by the pure
 * {@link resolveNavbar} resolver, so this component is a thin projection: the
 * logo (→ `/`), the resolved product links with the active section highlighted,
 * and a right cluster of design customizer + theme toggle + identity (the avatar
 * dropdown or a Log In button). On surfaces that expose the accent picker the
 * desktop cluster is the full `ThemeCustomizer` popover (accent + theme + cursor);
 * below `md` those surfaces drop the top-bar theme + language controls entirely
 * (the navbar drawer's appearance strip carries them), and the auth `minimal` bar
 * — which has no drawer — keeps a bare `ThemeSwitch` at every width. All of it
 * rides the existing cookie-backed switches (ADR-005), and the identity reuses the
 * shared `UserDropdown` — none of it is re-implemented here.
 */
function AppNavbar({
	variant,
	sidebarGroups,
	sidebarLabel,
}: {
	variant: NavbarVariant
	sidebarGroups?: SidebarGroup[]
	sidebarLabel?: string
}) {
	const { t } = useTranslation()
	const user = useOptionalUser()
	const requestInfo = useOptionalRequestInfo()
	const location = useLocation()
	const pending = useRoutePending()
	const nav: NavbarVisibility = resolveNavbar({
		variant,
		isLoggedIn: Boolean(user),
	})
	// The pure resolver decides *which* affordances show and their label keys; the
	// chrome translates every label at render off the `common` dictionary (ADR-074).
	const ctaLink = accountCtaLink(nav.account)
	// Scrollspy over the resolved anchor links (the landing in-page sections), so
	// the link for the section in view highlights as you scroll.
	const anchorIds = nav.productLinks
		.map((link) => link.anchorId)
		.filter((id): id is string => Boolean(id))
	const activeSection = useActiveSection(anchorIds)
	// Marketing owns a frosted-glass header. On the landing page — the only
	// marketing surface with a hero behind the nav — it floats transparent over
	// the hero at the very top, then settles into the frosted bar on scroll. Other
	// marketing pages (blog) have no hero, so they wear the frosted glass always.
	const isMarketing = variant === 'marketing'
	const scrolled = useScrolled()
	const isHome = isMarketing && location.pathname === '/'
	const frosted = isMarketing && (!isHome || scrolled)

	return (
		<header
			className={cn(
				'sticky top-0 z-40 transition-[background-color,border-color,box-shadow] duration-[var(--duration-base)] ease-out',
				isMarketing
					? // The hairline stays present in both states so its colour (not its
						// width) animates as the bar settles — no 1px layout jump on scroll.
						cn(
							'border-b',
							frosted
								? 'bg-background/70 border-border/60 shadow-raised backdrop-blur-md backdrop-saturate-150'
								: 'border-transparent bg-transparent',
						)
					: cn(
							'bg-background/80 backdrop-blur',
							// Minimal (auth) is borderless; every other surface carries the hairline.
							variant !== 'minimal' && 'border-border border-b',
						),
			)}
		>
			<nav
				aria-label="Primary"
				className="container flex h-15 items-center justify-between gap-6"
			>
				<div className="flex items-center gap-6">
					{/* The mobile menu — a hamburger below `md` on marketing/full only;
					    `minimal` (auth) has no links or section nav, so no drawer. */}
					{variant !== 'minimal' ? (
						<NavbarDrawer
							nav={nav}
							activeSection={activeSection}
							sidebarGroups={sidebarGroups}
							sidebarLabel={sidebarLabel}
						/>
					) : null}
					<Logo
						hideWordmarkOnMobile={variant !== 'minimal'}
						pending={pending}
					/>
					{nav.productLinks.length > 0 ? (
						<ul className="hidden items-center gap-6 md:flex">
							{nav.productLinks.map((link) => {
								const active = isLinkActive(link, {
									pathname: location.pathname,
									activeSection,
								})
								return (
									<li key={link.section}>
										<Link
											to={link.to}
											aria-current={active ? 'page' : undefined}
											// A brand underline that scales in from the left on hover
											// (and stays drawn for the active section) — the resting
											// state is server-rendered, so the animation is pure
											// enhancement on top of a plain coloured link.
											className={cn(
												'after:bg-brand hover:text-brand relative py-1 text-body-xs transition-colors duration-[var(--duration-fast)] ease-out',
												'after:absolute after:inset-x-0 after:-bottom-0.5 after:h-px after:origin-left after:transition-transform after:duration-[var(--duration-base)] after:ease-out hover:after:scale-x-100',
												active
													? 'text-brand after:scale-x-100'
													: 'text-muted-foreground after:scale-x-0',
											)}
										>
											{t(link.labelKey)}
										</Link>
									</li>
								)
							})}
						</ul>
					) : null}
				</div>

				<div className="flex items-center gap-2.5">
					{/* Language + theme ride the top bar on desktop, but below `md` the
					    navbar drawer carries them in its appearance strip — so on the
					    surfaces that *have* a drawer (marketing/full) the top-bar copies
					    hide on mobile to keep the bar uncluttered. The auth `minimal` bar
					    has no drawer, so its controls stay visible at every width. */}
					<div className={cn('flex', variant !== 'minimal' && 'hidden md:flex')}>
						<LanguageSwitch />
					</div>
					{nav.showAccentPicker ? (
						// Desktop only: the full design customizer popover (accent presets +
						// Hue/Chroma/Light sliders + theme + cursor). It subsumes the bare
						// accent swatches and theme toggle on these surfaces (ADR 062).
						// Below `md` the accent + theme controls live in the navbar drawer.
						<div className="hidden md:flex">
							<ThemeCustomizer
								accent={requestInfo?.userPrefs.accent ?? DEFAULT_ACCENT}
								cursor={requestInfo?.userPrefs.cursor ?? 'default'}
								theme={requestInfo?.userPrefs.theme ?? null}
							/>
						</div>
					) : (
						<ThemeSwitch userPreference={requestInfo?.userPrefs.theme ?? null} />
					)}
					{/* The bell rides beside the avatar — both show only for a logged-in
					    user on `full`/`marketing`, never on `minimal` (auth), which the
					    `avatar` identity slot already captures. */}
					{nav.account === 'avatar' ? (
						<>
							<NotificationBell />
							<UserDropdown />
						</>
					) : null}
					{ctaLink ? (
						<Button asChild variant="default">
							<Link to={ctaLink.to}>{t(ctaLink.labelKey)}</Link>
						</Button>
					) : null}
				</div>
			</nav>
		</header>
	)
}
